import { useState, type MouseEvent, type ReactNode } from 'react'
import {
  ChevronDown,
  Folder,
  LayoutGrid,
  LayoutList,
  List,
  MoreVertical,
  PanelRight,
  RefreshCw,
  SlidersHorizontal,
} from 'lucide-react'
import { AddressBar, type BreadcrumbSegment } from '@/components/ui/address-bar'
import { Button } from '@/components/ui/button'
import { IconButton } from '@/components/ui/icon-button'
import { NavigationButtons } from '@/components/ui/navigation-buttons'
import { cn } from '@/lib/utils'
import { ShellIndexRow } from '@/components/layout/shell/shell-index-row'

export type FolderViewMode = 'list' | 'grid' | 'card'
export type FolderKindFilter = 'all' | 'folder' | 'file'

const VIEW_MODES: { id: FolderViewMode; label: string; icon: typeof List }[] = [
  { id: 'card', label: 'Card view', icon: LayoutList },
  { id: 'grid', label: 'Grid view', icon: LayoutGrid },
  { id: 'list', label: 'List view', icon: List },
]

export function FolderViewModeButtons({
  mode,
  onChange,
  testId,
}: {
  mode: FolderViewMode
  onChange: (mode: FolderViewMode) => void
  testId?: string
}) {
  return (
    <div
      className="ml-auto flex items-center rounded-md border border-border p-0.5"
      role="group"
      aria-label="View mode"
      data-testid={testId}
    >
      {VIEW_MODES.map((item) => {
        const Icon = item.icon
        const on = mode === item.id
        return (
          <button
            key={item.id}
            type="button"
            title={item.label}
            aria-label={item.label}
            aria-pressed={on}
            onClick={() => onChange(item.id)}
            className={cn(
              'rounded p-1.5 transition-colors',
              on ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted',
            )}
          >
            <Icon className="h-4 w-4" />
          </button>
        )
      })}
    </div>
  )
}

export function FolderTitleRow({
  canGoBack,
  canGoForward,
  onGoBack,
  onGoForward,
  segments,
  title,
  icon,
  onReload,
  reloading = false,
  previewOpen,
  onTogglePreview,
  menuItems = [],
  testId,
  menuTestId,
}: {
  canGoBack: boolean
  canGoForward: boolean
  onGoBack?: () => void
  onGoForward?: () => void
  segments: BreadcrumbSegment[]
  title: string
  icon?: ReactNode
  onReload?: () => void
  reloading?: boolean
  previewOpen: boolean
  onTogglePreview: () => void
  menuItems?: Array<{ label: string; icon: ReactNode; onSelect: () => void }>
  testId?: string
  menuTestId?: string
}) {
  const [showMore, setShowMore] = useState(false)
  return (
    <nav
      className="flex items-center justify-between gap-2 border-b border-border bg-background/50 px-4 py-2"
      aria-label="Folder actions"
      data-testid={testId}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <NavigationButtons
          onGoBack={onGoBack}
          onGoForward={onGoForward}
          canGoBack={canGoBack}
          canGoForward={canGoForward}
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          {segments.length ? <AddressBar segments={segments} /> : null}
          <h1 className="flex min-w-0 items-center gap-2 text-lg font-semibold text-foreground">
            {icon ?? <Folder className="h-4 w-4 shrink-0 text-blue-500" aria-hidden />}
            <span className="truncate">{title}</span>
          </h1>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {onReload ? (
          <IconButton
            icon={<RefreshCw className={cn('h-4 w-4', reloading && 'animate-spin')} />}
            label={reloading ? 'Reloading folder' : 'Reload folder'}
            variant="ghost"
            size="sm"
            onClick={onReload}
            disabled={reloading}
            loading={reloading}
          />
        ) : null}
        <div className="relative">
          <button
            type="button"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-accent hover:text-accent-foreground"
            aria-label="More options"
            aria-expanded={showMore}
            data-testid={menuTestId}
            onClick={() => setShowMore((open) => !open)}
          >
            <MoreVertical className="h-4 w-4" />
          </button>
          {showMore ? (
            <div className="absolute right-0 top-full z-50 mt-1 min-w-[180px] rounded-md border border-border bg-background p-1 shadow-lg">
              {menuItems.map((item) => (
                <button
                  key={item.label}
                  type="button"
                  className="flex w-full items-center rounded px-2 py-1.5 text-sm hover:bg-muted"
                  onClick={() => {
                    item.onSelect()
                    setShowMore(false)
                  }}
                >
                  <span className="mr-2">{item.icon}</span>
                  {item.label}
                </button>
              ))}
              <button
                type="button"
                className="flex w-full items-center rounded px-2 py-1.5 text-sm hover:bg-muted"
                onClick={() => {
                  onTogglePreview()
                  setShowMore(false)
                }}
              >
                <PanelRight className="mr-2 h-4 w-4" />
                {previewOpen ? 'Hide preview pane' : 'Show preview pane'}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </nav>
  )
}

export function FolderFilterRow({
  viewMode,
  onViewMode,
  kindFilter,
  onKindFilter,
  sortDir,
  onSortDir,
  testId,
  viewModeTestId,
}: {
  viewMode: FolderViewMode
  onViewMode: (mode: FolderViewMode) => void
  kindFilter: FolderKindFilter
  onKindFilter: (kind: FolderKindFilter) => void
  sortDir: 'asc' | 'desc'
  onSortDir: (dir: 'asc' | 'desc') => void
  testId?: string
  viewModeTestId?: string
}) {
  const [showFilters, setShowFilters] = useState(false)
  const [showSort, setShowSort] = useState(false)
  const filterOn = kindFilter !== 'all'
  const sortLabel = sortDir === 'asc' ? 'Name' : 'Name, Z-A'
  return (
    <ShellIndexRow className="bg-background" aria-label="Document filters" testId={testId}>
      <div className="relative">
        <Button
          variant="outline"
          size="sm"
          className={cn('gap-1', filterOn && 'border-primary/50 text-primary')}
          aria-label="More filters"
          aria-expanded={showFilters}
          onClick={() => setShowFilters((open) => !open)}
        >
          <SlidersHorizontal className="h-3 w-3" />
          Filters
          {filterOn ? (
            <span className="ml-0.5 rounded-full bg-primary px-1 py-0 text-[10px] leading-4 text-primary-foreground">
              1
            </span>
          ) : null}
          <ChevronDown className="h-3 w-3" aria-hidden />
        </Button>
        {showFilters ? (
          <div className="absolute left-0 top-full z-50 mt-1 min-w-[160px] rounded-md border border-border bg-background p-1 shadow-lg">
            {(
              [
                ['all', 'All'],
                ['folder', 'Folders'],
                ['file', 'Files'],
              ] as const
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                className={cn(
                  'w-full rounded px-2 py-1.5 text-left text-sm hover:bg-muted',
                  kindFilter === id && 'bg-muted font-medium',
                )}
                onClick={() => {
                  onKindFilter(id)
                  setShowFilters(false)
                }}
              >
                {label}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      {viewMode === 'list' ? null : (
        <div className="relative">
          <Button
            variant="outline"
            size="sm"
            className="gap-1 border-border/60"
            aria-label={`Sort: ${sortLabel}`}
            aria-expanded={showSort}
            onClick={() => setShowSort((open) => !open)}
          >
            {sortLabel}
            <ChevronDown className="h-3 w-3" aria-hidden />
          </Button>
          {showSort ? (
            <div className="absolute left-0 top-full z-50 mt-1 min-w-[140px] rounded-md border border-border bg-background p-1 shadow-lg">
              {(
                [
                  ['asc', 'Name'],
                  ['desc', 'Name, Z-A'],
                ] as const
              ).map(([dir, label]) => (
                <button
                  key={dir}
                  type="button"
                  className={cn(
                    'w-full rounded px-2 py-1.5 text-left text-sm hover:bg-muted',
                    sortDir === dir && 'bg-muted font-medium',
                  )}
                  onClick={() => {
                    onSortDir(dir)
                    setShowSort(false)
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      )}
      <FolderViewModeButtons mode={viewMode} onChange={onViewMode} testId={viewModeTestId} />
    </ShellIndexRow>
  )
}

export function FolderItemCard({
  selected,
  stacked,
  title,
  subtitle,
  icon,
  preview,
  onSelect,
  onOpen,
  onContextMenu,
  testId,
}: {
  selected: boolean
  stacked: boolean
  title: string
  subtitle: string
  icon: ReactNode
  preview?: ReactNode
  onSelect: () => void
  onOpen: () => void
  onContextMenu?: (event: MouseEvent) => void
  testId?: string
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      data-testid={testId}
      onClick={onSelect}
      onDoubleClick={(event) => {
        event.preventDefault()
        onOpen()
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') onOpen()
      }}
      onContextMenu={onContextMenu}
      className={cn(
        'min-w-0 rounded-lg border p-3 text-left',
        stacked ? 'flex w-full items-start gap-3' : 'flex h-56 flex-col gap-2',
        selected
          ? 'border-primary bg-primary/5 ring-2 ring-primary'
          : 'border-border bg-card hover:border-primary',
      )}
    >
      {stacked ? null : (
        <div className="min-h-0 flex-1 overflow-hidden rounded bg-muted/30 p-2 text-xs text-muted-foreground [mask-image:linear-gradient(to_bottom,black_68%,transparent)]">
          {preview ?? subtitle}
        </div>
      )}
      <span className="flex min-w-0 items-start gap-2">
        <span className="mt-0.5 shrink-0">{icon}</span>
        <span className="min-w-0">
          <span className="block truncate text-sm font-medium">{title}</span>
          <span className="block truncate text-xs text-muted-foreground">{subtitle}</span>
        </span>
      </span>
    </div>
  )
}
